import { Flex, Text } from '@vtex/admin-ui'
import { useFormState, useWatch } from '@vtex/admin-ui-form'
import toIso2 from 'country-iso-3-to-2'
import { useEffect, useMemo } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { Item, ShippingLocation, ZipCodeRange } from 'typings'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from '../../../Drawer'
import { EMPTY_ZIP_CODE_RANGE } from '../../Scope/constants'
import { ShippingCoutriesSelector } from '../../Selectors'
import { messages } from './messages'
import styles from './styles'
import { suite } from './validation'
import { ZipCodeRanges } from './ZipCodeRanges'
const { vestResolver } = require('@hookform/resolvers/vest')

type ShippingLocationEditorProps = {
  initialValues: ShippingLocation
  drawerState: UseDrawerStateReturn
  onSave: (config?: ShippingLocation) => void
}

enum FORM_FIELDS {
  SHIPPING_COUNTRIES = 'shippingCountries',
  ZIP_CODE_RANGES = 'zipCodeRanges',
}

function getFormDefaultValues(
  initialValues: ShippingLocation,
  intl: IntlShape
): FormType {
  return {
    shippingCountries: initialValues.shippingCountries.map((code) => ({
      id: code,
      name: intl.formatDisplayName(toIso2(code), { type: 'region' }) ?? code,
    })),

    zipCodeRanges: initialValues.zipCodeRanges,
  }
}

export type FormType = {
  shippingCountries: Item[]
  zipCodeRanges: ZipCodeRange[]
}

export function ShippingLocationEditor(props: ShippingLocationEditorProps) {
  const intl = useIntl()
  const { $t } = intl
  const { initialValues: parentInitialValues, drawerState, onSave } = props

  const initialValues = useMemo(
    () => ({
      shippingCountries: parentInitialValues.shippingCountries,
      zipCodeRanges: parentInitialValues.zipCodeRanges.length
        ? parentInitialValues.zipCodeRanges
        : [EMPTY_ZIP_CODE_RANGE],
    }),
    [parentInitialValues]
  )

  const isCreating = !initialValues

  const drawerForm = useFormState<FormType>({
    defaultValues: getFormDefaultValues(initialValues, intl),
    resolver: vestResolver(suite({ intl })),
  })

  const [shippingCountries, zipCodeRanges]: [
    FormType[FORM_FIELDS.SHIPPING_COUNTRIES],
    FormType[FORM_FIELDS.ZIP_CODE_RANGES]
  ] = useWatch({
    form: drawerForm,
    //@ts-expect-error Type 'string[]' is not assignable to type 'string'
    name: [FORM_FIELDS.SHIPPING_COUNTRIES, FORM_FIELDS.ZIP_CODE_RANGES],
  })

  useEffect(() => {
    drawerState.open &&
      drawerForm.reset(getFormDefaultValues(initialValues, intl))
  }, [drawerState.open, drawerForm, initialValues, intl])

  async function handleSave() {
    const valid = await drawerForm.trigger()

    if (!valid) return

    onSave({
      shippingCountries: shippingCountries.map((item) => item.id),
      zipCodeRanges: zipCodeRanges.filter((range) => range.from && range.to),
    })

    drawerState.hide()
  }

  return (
    <Drawer state={drawerState}>
      <DrawerHeader>
        <DrawerTitle>{$t(messages.title)}</DrawerTitle>
        <DrawerSubtitle>{$t(messages.editor.subtitle)}</DrawerSubtitle>
        <DismissButton onHide={drawerState.hide} />
      </DrawerHeader>

      <DrawerContent>
        <Flex direction="column" className={styles.editor.content}>
          <span>
            <Text tone="secondary" className="label">
              <FormattedMessage {...messages.editor.shippingCountries} />
            </Text>

            <ShippingCoutriesSelector
              state={drawerForm}
              name={'shippingCountries'}
            />
          </span>

          <span>
            <Text tone="secondary" className="label">
              <FormattedMessage {...messages.editor.zipCodeRanges} />
            </Text>

            <ZipCodeRanges
              name={FORM_FIELDS.ZIP_CODE_RANGES}
              state={drawerForm}
            />
          </span>
        </Flex>
      </DrawerContent>

      <DrawerFooter>
        <CancelButton onClick={drawerState.hide} />
        <SaveButton onClick={handleSave} isCreating={isCreating} />
      </DrawerFooter>
    </Drawer>
  )
}
